import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Select,
  MenuItem,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  CssBaseline,
  useMediaQuery,
  useTheme,
  Checkbox,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { useStoreData } from '../useStoreData';
import { useLogoMaps } from '../useLogoMaps';
import { useOddsFormat, fmtPrice } from '../oddsFormat';
import { bookGroupMenuItems, resolveBooksSelection } from '../bookGroups';
import FuturesTutorial from './FuturesTutorial';

// ══════════════════════════════════════════════════════════
//  CONSTANTS (matching Odds.js/Props.js)
// ══════════════════════════════════════════════════════════

const PREFERRED_BOOK_ORDER = [
  'DraftKings', 'Pinnacle', 'Bookmaker', 'Circa', 'FanDuel', 'Bet365',
  'theScore Bet', 'Caesars', 'BetMGM', 'BetRivers', 'Bovada', 'Hard Rock',
  'Fanatics', 'Prophet Exchange', 'NoVig', 'Polymarket (USA)', 'Kalshi', 'LowVig',
];

const bookmakerLinks = {
  'Prophet Exchange': 'https://prophetx.onelink.me/E5Yi/kxa7a4q7',
  'NoVig': 'https://novig.onelink.me/JHQQ/1vgeffy0',
  'DraftKings': 'https://dksb.sng.link/As9kz/85o9?_dl=https%3A%2F%2Fsportsbook.draftkings.com%2Fgateway%3Fs%3D356902394%26wpcid%3D239969%26wpcn%3DBet5Win150%26wpsrc%3DBetsperts%26wpcrid%3D%26wpscn%3D&pcid=239969&psn=Betsperts&pcn=Bet5Win150_4For4',
  'FanDuel': 'https://wlfanduelus.adsrv.eacdn.com/C.ashx?btag=a_21545b_16c_&affid=11060&siteid=21545&adid=16&c=',
  'Bet365': 'https://www.bet365.com/olp/open-account?affiliate=365_02038136',
  'BetMGM': 'https://mediaserver.betmgmpartners.com/renderBanner.do?zoneId=1700764',
  'Caesars': 'https://wlwilliamhillus.adsrv.eacdn.com/C.ashx?btag=a_12260b_2091c_&affid=47&siteid=12260&adid=2091&c=',
  'Fanatics': 'https://go.affiliationcloud.com/0002JY',
};

// Date/time formatting (matching Odds.js/Props.js)
const DATE_FMT = new Intl.DateTimeFormat('en-US', { year: '2-digit', month: 'numeric', day: 'numeric' });
const TIME_FMT = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

// ══════════════════════════════════════════════════════════
//  THEME + STYLES
// ══════════════════════════════════════════════════════════

const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } },
    },
  },
});

const STYLE_BEST_IMG = { width: '30px', height: '30px', objectFit: 'contain' };

// Shared cell outer box style (like Props.js)
const SX_COL_OUTER = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  height: '100%',
  width: '100%',
};

// DataGrid styling
const DATA_GRID_SX = {
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#1e1e1e', borderBottom: '2px solid #444',
    position: 'sticky', top: 0, zIndex: 2,
  },
  '& .MuiDataGrid-columnHeader': {
    userSelect: 'none', cursor: 'move', outline: 'none !important',
    '&:focus': { outline: 'none !important' },
    '&:focus-within': { outline: 'none !important' },
  },
  '& .MuiDataGrid-columnHeader--alignRight': { justifyContent: 'flex-end !important' },
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    outline: 'none !important', '&:focus': { outline: 'none !important' },
  },
  '& .MuiDataGrid-cell': {
    color: '#ffffff !important', userSelect: 'none', padding: 0,
    borderRight: '1px solid rgba(255, 255, 255, 0.3)',
  },
  '& .MuiDataGrid-cell:last-child': { borderRight: 'none' },
  '& .MuiDataGrid-cell:focus': { outline: 'none' },
  '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
  '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
  '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#1a1a1a', '&:hover': { backgroundColor: '#1a1a1a' } },
  '& .MuiDataGrid-columnSeparator': { visibility: 'visible', color: 'rgba(255, 255, 255, 0.3)' },
  '& .MuiDataGrid-virtualScroller': { overflowY: 'auto !important' },
  '& .MuiDataGrid-root': { overflowY: 'auto !important', margin: 0 },
  '& .MuiDataGrid-main': { padding: 0 },
  '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
  '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { color: '#ffffff !important' },
  '& .MuiSelect-icon': { color: '#ffffff !important' },
  '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
  '& .MuiDataGrid-columnHeaderTitle': {
    color: '#ffffff !important', whiteSpace: 'nowrap',
    overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2,
  },
  '& .MuiDataGrid-columnHeaderDraggableContainer': { cursor: 'move' },
};

// Odds formatting (global US / % toggle)
const formatOdds = (odds, mode) => fmtPrice(odds, mode);

// Implied probability from American odds
const impliedProb = (odds) => {
  if (odds == null) return null;
  if (odds > 0) return 100 / (odds + 100);
  return Math.abs(odds) / (Math.abs(odds) + 100);
};

// Parse date string to Date object
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    return date;
  } catch {
    return null;
  }
};

// ══════════════════════════════════════════════════════════
//  TRANSFORM SERVER DATA TO GRID ROWS
// ══════════════════════════════════════════════════════════

/**
 * Check if a market has over/under selections.
 * Case-insensitive — backend stores selection_line as 'over'/'under' lowercase.
 */
const isOverUnderMarket = (market) => {
  if (!market.odds) return false;
  for (const oddsList of Object.values(market.odds)) {
    if (!Array.isArray(oddsList)) continue;
    for (const odd of oddsList) {
      const line = (odd.selection_line || '').toLowerCase();
      if (line === 'over' || line === 'under') return true;
    }
  }
  return false;
};

/**
 * Extract a clean display name from selection.
 * Removes "Over X.X" or "Under X.X" suffixes.
 */
const extractDisplayName = (odd, fallback) => {
  // Strip trailing "Over X.X" / "Under X.X" / "o5.5" / "u5.5" suffixes from
  // any text-based name. Used for selection/id-derived fallbacks.
  const stripLineSuffix = (s) => s
    .replace(/\s*[-:]\s*(over|under)\s*[\d.]+\s*$/i, '')
    .replace(/\s+(over|under)\s+[\d.]+\s*$/i, '')
    .replace(/\s+(o|u)[\d.]+\s*$/i, '')
    .trim();

  // Clean trailing separators/whitespace.
  const cleanTrailing = (s) => s.replace(/[\s\-:]+$/, '').trim();

  // 1. Prefer raw selection text — already human-formatted.
  if (odd.selection) {
    return cleanTrailing(stripLineSuffix(odd.selection)) || odd.selection;
  }

  // 2. Normalized selection — snake_case → Title Case.
  if (odd.normalized_selection) {
    return odd.normalized_selection
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  // 3. Derive from the odds id. Shape:
  //      "{league}:{book}:{market_slug}:{selection_slug}"
  //    The selection slug often contains the matchup for markets where
  //    `selection` is empty (e.g. Series Total Games bare format).
  if (odd.id) {
    const parts = String(odd.id).split(':');
    const last = parts[parts.length - 1] || '';
    // Remove trailing _over_X_X or _under_X_X
    const stripped = last.replace(/_(?:over|under)_[\d_]+$/i, '');
    if (stripped && stripped !== last) {
      const titled = stripped
        .replace(/_-_/g, ' - ')
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, c => c.toUpperCase())
        .replace(/\bVs\b/g, 'vs');
      const cleaned = cleanTrailing(titled);
      if (cleaned) return cleaned;
    }
  }

  // 4. Final fallback: the market name, stripped of any trailing "- Market Type"
  //    suffix that just repeats the dropdown label.
  if (fallback) {
    // If the market name looks like "X vs Y - Market Type", return "X vs Y".
    const dashIdx = fallback.lastIndexOf(' - ');
    if (dashIdx > 0) return fallback.slice(0, dashIdx).trim();
    return fallback;
  }
  return '';
};

/**
 * Transform futures market data into grid rows.
 * For over/under markets: one row per team/player with Over/Under stacked
 * For outright markets: one row per selection
 */
const transformMarketToRows = (market, sportsbooks, teamMap, bookLogos, pickemBookIds, isOU, oddsFormat) => {
  if (!market.odds || typeof market.odds !== 'object') return [];

  // Create sportsbook ID -> name lookup (non-pickem only)
  const bookIdToName = {};
  if (sportsbooks && sportsbooks.length > 0) {
    sportsbooks.forEach(sb => {
      if (sb.id && sb.name && !sb.pickem) {
        bookIdToName[sb.id] = sb.name;
      }
    });
  }

  // Parse market dates
  const startDate = parseDate(market.start_date);
  const startDateFormatted = startDate ? {
    date: DATE_FMT.format(startDate),
    time: TIME_FMT.format(startDate),
  } : { date: '', time: '' };

  if (isOU) {
    // Over/Under market: group by base selection (team/player)
    const selectionGroups = new Map(); // displayName -> { Over: {...}, Under: {...}, bookOdds: {...} }

    for (const [sportsbookId, oddsList] of Object.entries(market.odds)) {
      if (!Array.isArray(oddsList)) continue;
      if (pickemBookIds.has(sportsbookId)) continue;

      const bookName = bookIdToName[sportsbookId];
      if (!bookName) continue;

      for (const odd of oddsList) {
        const displayName = extractDisplayName(odd, market.name);
        if (!displayName) continue;

        // Normalize side — backend may store 'over'/'under' lowercase
        const rawLine = (odd.selection_line || '').toLowerCase();
        if (rawLine !== 'over' && rawLine !== 'under') continue;
        const side = rawLine === 'over' ? 'Over' : 'Under';

        if (!selectionGroups.has(displayName)) {
          selectionGroups.set(displayName, {
            displayName,
            teamId: odd.team_id,
            playerId: odd.player_id,
            Over: { prices: {}, points: null, bestPrice: -Infinity, bestBook: '', bestBookLogo: '' },
            Under: { prices: {}, points: null, bestPrice: -Infinity, bestBook: '', bestBookLogo: '' },
          });
        }

        const group = selectionGroups.get(displayName);
        const sideData = group[side];

        // Store price for this book
        const existingPrice = sideData.prices[bookName];
        if (existingPrice === undefined || odd.price > existingPrice) {
          sideData.prices[bookName] = odd.price;
        }

        // Track points (line)
        if (odd.points != null) {
          sideData.points = odd.points;
        }

        // Update best price
        if (odd.price > sideData.bestPrice) {
          sideData.bestPrice = odd.price;
          sideData.bestBook = bookName;
          sideData.bestBookLogo = bookLogos?.[bookName] || '';
        }
      }
    }

    // Convert to rows
    const rows = [];
    for (const [displayName, group] of selectionGroups) {
      const teamData = teamMap?.[group.teamId];
      const teamLogo = teamData?.logo || '';

      // Build bookOdds structure like Props.js: { bookName: { Over: {...}, Under: {...} } }
      const bookOdds = {};
      const allBooks = new Set([...Object.keys(group.Over.prices), ...Object.keys(group.Under.prices)]);
      for (const bookName of allBooks) {
        bookOdds[bookName] = {
          Over: group.Over.prices[bookName] != null ? {
            price: group.Over.prices[bookName],
            lineDisplay: group.Over.points != null ? `o${group.Over.points}` : '',
          } : null,
          Under: group.Under.prices[bookName] != null ? {
            price: group.Under.prices[bookName],
            lineDisplay: group.Under.points != null ? `u${group.Under.points}` : '',
          } : null,
        };
      }

      // Build bestOdds structure
      const bestOdds = {
        Over: group.Over.bestPrice > -Infinity ? {
          display: `${group.Over.points != null ? `o${group.Over.points} ` : ''}${formatOdds(group.Over.bestPrice, oddsFormat)}`,
          price: group.Over.bestPrice,
          logo: group.Over.bestBookLogo,
          book: group.Over.bestBook,
          impliedProb: impliedProb(group.Over.bestPrice),
        } : null,
        Under: group.Under.bestPrice > -Infinity ? {
          display: `${group.Under.points != null ? `u${group.Under.points} ` : ''}${formatOdds(group.Under.bestPrice, oddsFormat)}`,
          price: group.Under.bestPrice,
          logo: group.Under.bestBookLogo,
          book: group.Under.bestBook,
          impliedProb: impliedProb(group.Under.bestPrice),
        } : null,
      };

      // Sort priority: use best Over price (favorites first)
      const sortPrice = group.Over.bestPrice > -Infinity ? group.Over.bestPrice : group.Under.bestPrice;

      rows.push({
        id: `${market.future_id}-${displayName}`,
        displayName,
        teamId: group.teamId,
        teamLogo,
        playerId: group.playerId,
        isOverUnder: true,
        startDate,
        startDateFormatted,
        bestOdds,
        bookOdds,
        sortPrice,
      });
    }

    return rows;
  } else {
    // Outright market: one row per selection
    const selectionMap = new Map();

    for (const [sportsbookId, oddsList] of Object.entries(market.odds)) {
      if (!Array.isArray(oddsList)) continue;
      if (pickemBookIds.has(sportsbookId)) continue;

      const bookName = bookIdToName[sportsbookId];
      if (!bookName) continue;

      for (const odd of oddsList) {
        // Build the display name: selection + disambiguator.
        // The disambiguator is either selection_line (for correct-score markets
        // like "Cleveland Cavaliers 4:1") or points (for handicap/spread markets
        // like "Cleveland Cavaliers -1.5"). Over/under lines are handled in the
        // OU branch and don't append here.
        let baseName = odd.selection;
        if (!baseName && odd.normalized_selection) {
          baseName = odd.normalized_selection
            .replace(/_/g, ' ')
            .replace(/\b\w/g, c => c.toUpperCase());
        }
        const line = odd.selection_line;
        const lineIsOU = line && /^(over|under)$/i.test(line);
        let suffix = '';
        if (line && !lineIsOU) {
          suffix = ` ${line}`;
        } else if (odd.points != null) {
          // Positive points need an explicit '+' sign; negative already has '-'.
          const p = odd.points;
          suffix = ` ${p > 0 ? '+' : ''}${p}`;
        }
        const displayName = baseName ? `${baseName}${suffix}` : '';
        const key = displayName || odd.normalized_selection;
        if (!key) continue;

        if (!selectionMap.has(key)) {
          selectionMap.set(key, {
            selection: displayName,
            displayName: displayName,
            teamId: odd.team_id,
            playerId: odd.player_id,
            bookOdds: {},
            bestPrice: -Infinity,
            bestBook: '',
            bestBookLogo: '',
          });
        }

        const entry = selectionMap.get(key);
        const existingPrice = entry.bookOdds[bookName];
        if (existingPrice === undefined || odd.price > existingPrice) {
          entry.bookOdds[bookName] = odd.price;
        }

        if (odd.price > entry.bestPrice) {
          entry.bestPrice = odd.price;
          entry.bestBook = bookName;
          entry.bestBookLogo = bookLogos?.[bookName] || '';
        }
      }
    }

    // Convert to rows
    const rows = [];
    for (const [key, data] of selectionMap) {
      if (data.bestPrice === -Infinity) continue;

      const teamData = teamMap?.[data.teamId];
      const teamLogo = teamData?.logo || '';

      rows.push({
        id: `${market.future_id}-${key}`,
        displayName: data.displayName,
        teamId: data.teamId,
        teamLogo,
        playerId: data.playerId,
        isOverUnder: false,
        startDate,
        startDateFormatted,
        bestOdds: {
          price: data.bestPrice,
          display: formatOdds(data.bestPrice, oddsFormat),
          logo: data.bestBookLogo,
          book: data.bestBook,
          impliedProb: impliedProb(data.bestPrice),
        },
        bookOdds: data.bookOdds,
        sortPrice: data.bestPrice,
      });
    }

    return rows;
  }
};

// ══════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════

// Selected books live under ONE key. They used to be split by breakpoint, but
// useMediaQuery reports false on the first render, so on phones the page read
// the desktop list and then the persist effect wrote it over the mobile one.
const BOOKS_KEY = 'futuresSelectedBooks';
const LEGACY_BOOKS_KEYS = ['futuresSelectedBooks_desktop', 'futuresSelectedBooks_mobile'];

// Returns the saved array, or null if nothing valid is stored.
const readSavedBooks = () => {
  for (const key of [BOOKS_KEY, ...LEGACY_BOOKS_KEYS]) {
    try {
      const parsed = JSON.parse(localStorage.getItem(key));
      if (Array.isArray(parsed)) return parsed;
    } catch { /* missing/corrupt — try the next key */ }
  }
  return null;
};

const Futures = ({ userName, isAuthenticated }) => {
  const oddsFormat = useOddsFormat();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const apiRef = useGridApiRef();

  // ── Get data from store ──
  const futuresMarkets = useStoreData('futures');
  const futuresLeagues = useStoreData('futuresLeagues');
  const leaguesConfig = useStoreData('leagues') || [];
  const sportsbooks = useStoreData('sportsbooks');
  const { bookLogos, teamMap } = useLogoMaps();

  // ── Build pickem book IDs set ──
  const pickemBookIds = useMemo(() => {
    const ids = new Set();
    if (sportsbooks && sportsbooks.length > 0) {
      sportsbooks.forEach(sb => {
        if (sb.pickem && sb.id) ids.add(sb.id);
      });
    }
    return ids;
  }, [sportsbooks]);

  // ── Build league ID -> pretty name lookup ──
  const leagueNameMap = useMemo(() => {
    const map = {};
    leaguesConfig.forEach(l => {
      if (l.id) map[l.id.toLowerCase()] = l.name || l.id;
    });
    return map;
  }, [leaguesConfig]);

  // ── Build available books list (excluding pickem) ──
  const availableBooks = useMemo(() => {
    if (!sportsbooks || sportsbooks.length === 0) return PREFERRED_BOOK_ORDER;
    return sportsbooks
      .filter(sb => !sb.pickem)
      .map(sb => sb.name)
      .filter(Boolean)
      .sort((a, b) => {
        const aIdx = PREFERRED_BOOK_ORDER.indexOf(a);
        const bIdx = PREFERRED_BOOK_ORDER.indexOf(b);
        if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
        if (aIdx === -1) return 1;
        if (bIdx === -1) return -1;
        return aIdx - bIdx;
      });
  }, [sportsbooks]);

  // ── Derive available leagues from data ──
  const availableLeagues = useMemo(() => {
    if (!futuresMarkets || futuresMarkets.length === 0) return [];
    const leagueSet = new Set();
    futuresMarkets.forEach(m => {
      const lid = m.league_id || m._league;
      if (lid) leagueSet.add(lid.toLowerCase());
    });
    return Array.from(leagueSet).sort((a, b) => {
      const nameA = leagueNameMap[a] || a;
      const nameB = leagueNameMap[b] || b;
      return nameA.localeCompare(nameB);
    });
  }, [futuresMarkets, leagueNameMap]);

  // ── Filter state ──
  const [league, setLeague] = useState(() => {
    const saved = localStorage.getItem('futuresLeague');
    return saved ? saved.toLowerCase() : '';
  });
  const [selectedMarket, setSelectedMarket] = useState(() => {
    return localStorage.getItem('futuresMarket') || '';
  });
  const [selectedBooks, setSelectedBooks] = useState(readSavedBooks);

  // ── Initialize league when data loads ──
  useEffect(() => {
    if (availableLeagues.length > 0 && (!league || !availableLeagues.includes(league))) {
      const defaultLeague = availableLeagues.includes('nba') ? 'nba' : availableLeagues[0];
      setLeague(defaultLeague);
    }
  }, [availableLeagues, league]);

  // ── Initialize selected books ──
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!initializedRef.current && availableBooks.length > 0 && selectedBooks === null) {
      setSelectedBooks(availableBooks);
      initializedRef.current = true;
    }
  }, [availableBooks, selectedBooks]);

  // ── Persist filter state ──
  useEffect(() => { localStorage.setItem('futuresLeague', league); }, [league]);
  useEffect(() => { localStorage.setItem('futuresMarket', selectedMarket); }, [selectedMarket]);
  useEffect(() => {
    if (selectedBooks === null) return;
    try {
      localStorage.setItem(BOOKS_KEY, JSON.stringify(selectedBooks));
      LEGACY_BOOKS_KEYS.forEach(k => localStorage.removeItem(k)); // one-time cleanup of the split-key era
    } catch { /* storage unavailable */ }
  }, [selectedBooks]);

  // ── Filter markets by league ──
  const leagueMarkets = useMemo(() => {
    if (!futuresMarkets || futuresMarkets.length === 0 || !league) return [];
    return futuresMarkets.filter(m => {
      const lid = (m.league_id || m._league || '').toLowerCase();
      return lid === league;
    });
  }, [futuresMarkets, league]);

  // ── Derive market options (sorted by date) ──
  const marketOptionsWithMeta = useMemo(() => {
    const marketMeta = new Map();
    leagueMarkets.forEach(m => {
      if (m.name) {
        const existingMeta = marketMeta.get(m.name);
        const thisDate = parseDate(m.start_date);
        if (!existingMeta) {
          marketMeta.set(m.name, { name: m.name, startDate: thisDate });
        } else if (thisDate && (!existingMeta.startDate || thisDate < existingMeta.startDate)) {
          existingMeta.startDate = thisDate;
        }
      }
    });
    return Array.from(marketMeta.values()).sort((a, b) => {
      if (a.startDate && b.startDate) {
        const dateDiff = a.startDate - b.startDate;
        if (dateDiff !== 0) return dateDiff;
        // Same event date → fall back to alphabetical (numeric-aware so
        // "2nd Overall Pick" sorts before "10th Overall Pick").
        return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
      }
      if (a.startDate) return -1;
      if (b.startDate) return 1;
      return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [leagueMarkets]);

  const marketOptions = useMemo(() => marketOptionsWithMeta.map(m => m.name), [marketOptionsWithMeta]);

  // ── Auto-select first market when league changes ──
  useEffect(() => {
    if (marketOptions.length > 0) {
      if (!selectedMarket || !marketOptions.includes(selectedMarket)) {
        setSelectedMarket(marketOptions[0]);
      }
    }
  }, [marketOptions, selectedMarket]);

  // ── Check if current market is over/under type ──
  const currentMarketIsOU = useMemo(() => {
    if (!selectedMarket || leagueMarkets.length === 0) return false;
    const matchingMarkets = leagueMarkets.filter(m => m.name === selectedMarket);
    return matchingMarkets.some(m => isOverUnderMarket(m));
  }, [selectedMarket, leagueMarkets]);

  // ── Transform data to grid rows ──
  const futuresData = useMemo(() => {
    if (!selectedMarket || leagueMarkets.length === 0) return [];

    const matchingMarkets = leagueMarkets.filter(m => m.name === selectedMarket);
    const allRows = [];

    for (const market of matchingMarkets) {
      const isOU = isOverUnderMarket(market);
      const rows = transformMarketToRows(market, sportsbooks, teamMap, bookLogos, pickemBookIds, isOU, oddsFormat);
      allRows.push(...rows);
    }

    // Dedupe by displayName
    const seen = new Map();
    for (const row of allRows) {
      const existing = seen.get(row.displayName);
      if (!existing) {
        seen.set(row.displayName, row);
      } else {
        // Merge bookOdds
        if (row.isOverUnder) {
          for (const [bookName, odds] of Object.entries(row.bookOdds)) {
            if (!existing.bookOdds[bookName]) {
              existing.bookOdds[bookName] = odds;
            } else {
              if (odds.Over && (!existing.bookOdds[bookName].Over || odds.Over.price > existing.bookOdds[bookName].Over.price)) {
                existing.bookOdds[bookName].Over = odds.Over;
              }
              if (odds.Under && (!existing.bookOdds[bookName].Under || odds.Under.price > existing.bookOdds[bookName].Under.price)) {
                existing.bookOdds[bookName].Under = odds.Under;
              }
            }
          }
        } else {
          for (const [bookName, price] of Object.entries(row.bookOdds)) {
            if (existing.bookOdds[bookName] === undefined || price > existing.bookOdds[bookName]) {
              existing.bookOdds[bookName] = price;
            }
          }
        }
      }
    }

    const dedupedRows = Array.from(seen.values());
    dedupedRows.sort((a, b) => a.sortPrice - b.sortPrice);
    return dedupedRows;
  }, [selectedMarket, leagueMarkets, sportsbooks, teamMap, bookLogos, pickemBookIds, oddsFormat]);

  // ── Handlers ──
  const handleBooksFilterChange = useCallback((event) => {
    const value = event.target.value;
    const groupSel = resolveBooksSelection(value, availableBooks);
    if (groupSel) {
      setSelectedBooks(groupSel);
    } else if (value.includes('all')) {
      setSelectedBooks(prev => (prev || []).length === availableBooks.length ? [] : availableBooks);
    } else {
      setSelectedBooks(value);
    }
  }, [availableBooks]);

  const renderBooksValue = useCallback((selected) => {
    if (!selected || selected.length === 0) return 'None';
    if (selected.length === availableBooks.length) return 'All Books';
    if (selected.length <= 2) return selected.join(', ');
    return `${selected.length} books`;
  }, [availableBooks]);

  // ══════════════════════════════════════════════════════════
  //  COLUMNS
  // ══════════════════════════════════════════════════════════

  const columns = useMemo(() => {
    const visibleBooks = (selectedBooks || []).filter(b => availableBooks.includes(b));
    const isOU = currentMarketIsOU;
    const rowHalfHeight = isOU ? '35px' : '70px';

    const baseColumns = [
      // Selection name column
      {
        field: 'displayName',
        headerName: '',
        minWidth: isMobile ? 120 : 230,
        flex: 1,
        sortable: false,
        disableReorder: true,
        renderCell: (params) => (
          <Box sx={SX_COL_OUTER}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: rowHalfHeight, pl: isMobile ? '4px' : '8px' }}>
              {params.row.teamLogo && (
                <img src={params.row.teamLogo} alt="" style={{ width: '24px', height: '24px', objectFit: 'contain', marginRight: '8px' }} />
              )}
              <Typography sx={{ fontSize: isMobile ? '12px' : '14px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                {params.value}
              </Typography>
            </Box>
            {isOU && <Box sx={{ height: '35px' }} />}
          </Box>
        ),
      },
      // Date/time column — ALWAYS stacks date on top, time on bottom (matching Odds/Props)
      {
        field: 'startDateFormatted',
        headerName: '',
        minWidth: isMobile ? 70 : 85,
        maxWidth: isMobile ? 70 : 85,
        sortable: false,
        disableReorder: true,
        renderCell: (params) => {
          const { date, time } = params.value || {};
          return (
            <Box sx={SX_COL_OUTER}>
              <Box sx={{ height: '35px', display: 'flex', alignItems: 'center', paddingLeft: isMobile ? '0px' : '8px', paddingRight: isMobile ? '4px' : '8px' }}>
                <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>{date || ''}</Typography>
              </Box>
              <Box sx={{ height: '35px', display: 'flex', alignItems: 'center', paddingLeft: isMobile ? '0px' : '8px', paddingRight: isMobile ? '4px' : '8px' }}>
                <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>{time || ''}</Typography>
              </Box>
            </Box>
          );
        },
      },
      // Best odds column
      {
        field: 'bestOdds',
        headerName: 'Best Odds',
        headerAlign: 'right',
        minWidth: isMobile ? 120 : 170,
        sortable: false,
        disableReorder: true,
        renderHeader: () => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', pr: '8px' }}>
            <Typography sx={{ fontSize: isMobile ? '12px' : '14px', color: '#ffffff' }}>Best Odds</Typography>
          </Box>
        ),
        renderCell: (params) => {
          const value = params.value;
          if (isOU) {
            // Over/Under stacked
            return (
              <Box sx={SX_COL_OUTER}>
                {['Over', 'Under'].map(side => {
                  const data = value?.[side];
                  if (!data) return <Box key={side} sx={{ height: '35px' }} />;
                  return (
                    <Box key={side} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '35px', pr: '8px', gap: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Typography sx={{ fontSize: isMobile ? '12px' : '14px', lineHeight: 1.2 }}>{data.display}</Typography>
                      </Box>
                      {data.logo && (
                        <Tooltip title={data.book} placement="top" arrow>
                          <img src={data.logo} alt="" style={STYLE_BEST_IMG} />
                        </Tooltip>
                      )}
                    </Box>
                  );
                })}
              </Box>
            );
          } else {
            // Outright single line
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%', pr: '8px', gap: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Typography sx={{ fontSize: isMobile ? '12px' : '14px', lineHeight: 1.2 }}>{value?.display || ''}</Typography>
                </Box>
                {value?.logo && (
                  <Tooltip title={value.book} placement="top" arrow>
                    <img src={value.logo} alt="" style={STYLE_BEST_IMG} />
                  </Tooltip>
                )}
              </Box>
            );
          }
        },
      },
    ];

    // Book columns
    const bookColumns = visibleBooks.map(book => ({
      field: book,
      headerName: '',
      headerAlign: 'right',
      minWidth: isMobile ? 85 : 110,
      flex: 0.5,
      sortable: false,
      disableReorder: false,
      renderHeader: () => {
        const hasLink = !!bookmakerLinks[book];
        const logo = bookLogos?.[book];
        return (
          <Box
            sx={{
              display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
              width: '100%', height: '100%', pr: isMobile ? '4px' : '12px',
              cursor: 'grab', '&:active': { cursor: 'grabbing' }, userSelect: 'none',
            }}
            onClick={(e) => {
              if (hasLink && e.detail > 0) window.open(bookmakerLinks[book], '_blank', 'noopener,noreferrer');
            }}
          >
            {logo ? (
              <Tooltip title={book} placement="bottom" arrow>
                <img src={logo} alt={book} style={{ maxWidth: isMobile ? '40px' : '60px', maxHeight: isMobile ? '28px' : '36px', width: 'auto', height: 'auto', objectFit: 'contain', pointerEvents: 'none', userSelect: 'none' }} />
              </Tooltip>
            ) : (
              <Typography sx={{ fontSize: isMobile ? '10px' : '12px', color: '#ffffff', textAlign: 'right' }}>{book}</Typography>
            )}
          </Box>
        );
      },
      renderCell: (params) => {
        const bookData = params.row.bookOdds?.[book];

        if (isOU) {
          // Over/Under stacked
          return (
            <Box sx={SX_COL_OUTER}>
              {['Over', 'Under'].map(side => {
                const data = bookData?.[side];
                const isBest = params.row.bestOdds?.[side]?.book === book;

                if (!data) {
                  return (
                    <Box key={side} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '35px', pr: '12px' }}>
                      <Typography sx={{ fontSize: '14px', color: '#555' }}>—</Typography>
                    </Box>
                  );
                }

                return (
                  <Box key={side} sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                    height: '35px', pr: isMobile ? '4px' : '12px',
                    border: isBest ? '2px solid #00cc00' : 'none',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                  }}>
                    <Typography sx={{ fontSize: isMobile ? '11px' : '13px', color: '#ffffff' }}>
                      {data.lineDisplay} {formatOdds(data.price, oddsFormat)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          );
        } else {
          // Outright single line
          const price = bookData;
          const isBest = params.row.bestOdds?.book === book;

          if (price == null) {
            return (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '100%', pr: '12px' }}>
                <Typography sx={{ fontSize: '14px', color: '#555' }}>—</Typography>
              </Box>
            );
          }

          return (
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              height: '100%', width: '100%', pr: isMobile ? '4px' : '12px',
              border: isBest ? '2px solid #00cc00' : 'none',
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
            }}>
              <Typography sx={{ fontSize: isMobile ? '12px' : '14px', color: '#ffffff' }}>{formatOdds(price, oddsFormat)}</Typography>
            </Box>
          );
        }
      },
    }));

    return [...baseColumns, ...bookColumns];
  }, [isMobile, selectedBooks, availableBooks, bookLogos, currentMarketIsOU, oddsFormat]);

  // ══════════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════════

  const prettyLeagueName = leagueNameMap[league] || league?.toUpperCase() || '';

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: 'background.default' }}>
          <FuturesTutorial />
          {/* Filter Section */}
          <Box className="filter-section" sx={{ padding: isMobile ? '8px' : '16px' }}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? 1 : 2} alignItems={isMobile ? 'stretch' : 'center'}>
              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 120 }}>
                <InputLabel id="league-filter-label" shrink sx={{ color: '#ffffff' }}>League</InputLabel>
                <Select labelId="league-filter-label" label="League" notched value={league} onChange={(e) => setLeague(e.target.value)}>
                  {availableLeagues.map(lg => (
                    <MenuItem key={lg} value={lg}>{leagueNameMap[lg] || lg.toUpperCase()}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 280 }}>
                <InputLabel id="market-filter-label" shrink sx={{ color: '#ffffff' }}>Market</InputLabel>
                <Select labelId="market-filter-label" label="Market" notched value={selectedMarket} onChange={(e) => setSelectedMarket(e.target.value)}>
                  {marketOptions.map(name => (
                    <MenuItem key={name} value={name}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 140 }}>
                <InputLabel id="books-filter-label" shrink sx={{ color: '#ffffff' }}>Books</InputLabel>
                <Select multiple labelId="books-filter-label" label="Books" notched value={selectedBooks || []} onChange={handleBooksFilterChange} renderValue={renderBooksValue}
                  MenuProps={{ autoFocus: false, disableAutoFocusItem: true, PaperProps: { style: { maxHeight: 320 } } }}>
                  <MenuItem value="all">
                    <Checkbox checked={availableBooks.length > 0 && (selectedBooks || []).length === availableBooks.length} />
                    <ListItemText primary="Select All" />
                  </MenuItem>
                  {bookGroupMenuItems(availableBooks)}
                  {availableBooks.map(book => (
                    <MenuItem key={book} value={book}>
                      <Checkbox checked={(selectedBooks || []).includes(book)} />
                      <ListItemText primary={book} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>

          {/* Table Section */}
          <Box className="table-section" sx={{ height: isMobile ? 'calc(100vh - 120px)' : 'calc(100vh - 88px)', overflowY: 'auto' }}>
            <Box sx={{ width: '100%', height: '100%' }}>
              {futuresData.length === 0 ? (
                <Box className="empty-state" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body1">
                    {futuresMarkets && futuresMarkets.length > 0
                      ? (marketOptions.length === 0 ? `No futures markets found for ${prettyLeagueName}.` : 'No odds available for the selected market.')
                      : 'Loading futures data...'}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <DataGridPro
                      apiRef={apiRef}
                      rows={futuresData}
                      columns={columns}
                      disableSelectionOnClick
                      disableRowSelectionOnClick
                      disableColumnSort
                      disableColumnMenu
                      disableColumnSelector
                      disableColumnFilter
                      hideFooter
                      rowHeight={70}
                      sx={DATA_GRID_SX}
                      rowBuffer={10}
                      rowThreshold={5}
                    />
                  </Box>
                  <Box sx={{ backgroundColor: '#1e1e1e', borderTop: '1px solid #444', px: 2, py: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>Total Rows: {futuresData.length}</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default Futures;